import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import type { ApiError } from '@/api/client'
import { onboardingApi } from '@/api/endpoints'
import { Button } from '@/components/ui/Button'
import { Combobox } from '@/components/ui/Select'
import { Switch } from '@/components/ui/Toggle'
import { formatCents } from '@/features/billing/planUi'
import { usePlans } from '@/features/billing/useLicense'
import { cn } from '@/lib/utils'
import { isLicenseError } from '@/store/license'
import { toast } from '@/store/toast'
import type { ApplicationSettings, AutoApplySchedule, Weekday } from '@/types/api'

const WEEKDAYS: Array<{ value: Weekday; short: string; label: string }> = [
  { value: 'mon', short: 'Mon', label: 'Monday' },
  { value: 'tue', short: 'Tue', label: 'Tuesday' },
  { value: 'wed', short: 'Wed', label: 'Wednesday' },
  { value: 'thu', short: 'Thu', label: 'Thursday' },
  { value: 'fri', short: 'Fri', label: 'Friday' },
  { value: 'sat', short: 'Sat', label: 'Saturday' },
  { value: 'sun', short: 'Sun', label: 'Sunday' },
]
const ORDER = WEEKDAYS.map((day) => day.value)
const WORKWEEK: Weekday[] = ['mon', 'tue', 'wed', 'thu', 'fri']

const browserTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

function timeZones(current: string): string[] {
  let zones: string[] = []
  try {
    const intl = Intl as unknown as { supportedValuesOf?: (key: string) => string[] }
    zones = intl.supportedValuesOf?.('timeZone') ?? []
  } catch {
    zones = []
  }
  if (!zones.includes(current)) zones = [current, ...zones]
  if (!zones.includes('UTC')) zones = [...zones, 'UTC']
  return zones
}

const sortDays = (days: Weekday[]) => [...days].sort((a, b) => ORDER.indexOf(a) - ORDER.indexOf(b))

/** "Mon–Fri, 09:00–18:00 (Asia/Kolkata)". */
export function describeSchedule(schedule: AutoApplySchedule | null | undefined): string {
  if (!schedule) return 'Any time'
  const days = sortDays(schedule.days)
  const key = days.join(',')
  const dayText =
    key === ORDER.join(',')
      ? 'Every day'
      : key === WORKWEEK.join(',')
        ? 'Mon–Fri'
        : days.map((day) => WEEKDAYS.find((w) => w.value === day)?.short ?? day).join(', ')
  return `${dayText}, ${schedule.start}–${schedule.end} (${schedule.timezone.replace(/_/g, ' ')})`
}

/**
 * Power: the days and hours Auto Apply runs, in the user's time zone.
 * "Any time" saves `null`. Mount with `key={settings.updatedAt}` so a save
 * (here or elsewhere) starts the form from what is stored.
 */
export function AutoApplyScheduleCard({ settings }: { settings: ApplicationSettings }) {
  const queryClient = useQueryClient()
  const saved = settings.autoApplySchedule ?? null
  const [anyTime, setAnyTime] = useState(saved === null)
  const [days, setDays] = useState<Weekday[]>(saved?.days?.length ? sortDays(saved.days) : WORKWEEK)
  const [start, setStart] = useState(saved?.start ?? '09:00')
  const [end, setEnd] = useState(saved?.end ?? '18:00')
  const [timezone, setTimezone] = useState(saved?.timezone ?? browserTimeZone())
  const zoneOptions = useMemo(
    () => timeZones(timezone).map((zone) => ({ value: zone, label: zone.replace(/_/g, ' ') })),
    // The list only needs building once; the current zone is always in it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const draft: AutoApplySchedule | null = anyTime ? null : { days: sortDays(days), start, end, timezone }
  const dirty = JSON.stringify(draft) !== JSON.stringify(saved ? { ...saved, days: sortDays(saved.days) } : null)
  const error = anyTime
    ? null
    : days.length === 0
      ? 'Pick at least one day.'
      : !start || !end
        ? 'Set a start and an end time.'
        : start >= end
          ? 'The end time must be after the start time.'
          : null

  const save = useMutation({
    mutationFn: (schedule: AutoApplySchedule | null) =>
      onboardingApi.saveSettings({ ...settings, autoApplySchedule: schedule }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['application-settings'], updated)
      toast.success('Schedule saved', describeSchedule(updated.autoApplySchedule ?? null))
    },
    onError: (caught: ApiError) => {
      if (!isLicenseError(caught)) toast.error('Could not save the schedule', caught.message)
    },
  })

  const toggleDay = (day: Weekday) =>
    setDays((current) => (current.includes(day) ? current.filter((d) => d !== day) : sortDays([...current, day])))

  return (
    <section className="card overflow-hidden" aria-label="Auto apply schedule">
      <div className="flex min-h-[56px] items-center justify-between gap-3 px-4 py-3 sm:px-5">
        <div className="min-w-0">
          <p className="text-[15px] font-semibold text-ink">Schedule</p>
          <p className="truncate text-[12px] text-ink-muted">{describeSchedule(saved)}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2 text-[13px] font-medium text-ink-soft">
          <span aria-hidden="true">Any time</span>
          <Switch checked={anyTime} onChange={setAnyTime} label="Any time" size="sm" />
        </div>
      </div>

      {!anyTime && (
        <div className="space-y-4 border-t border-line px-4 py-4 sm:px-5">
          <div>
            <div className="mb-2 flex items-center justify-between gap-2">
              <p className="label-eyebrow">Days</p>
              <div className="flex gap-1">
                <button type="button" onClick={() => setDays(WORKWEEK)} className="rounded-[7px] px-2 py-1 text-[12px] font-medium text-ink-muted hover:bg-surface-muted hover:text-ink">
                  Weekdays
                </button>
                <button type="button" onClick={() => setDays(ORDER)} className="rounded-[7px] px-2 py-1 text-[12px] font-medium text-ink-muted hover:bg-surface-muted hover:text-ink">
                  Every day
                </button>
              </div>
            </div>
            <div className="grid grid-cols-7 gap-1" role="group" aria-label="Days Auto Apply runs">
              {WEEKDAYS.map((day) => {
                const selected = days.includes(day.value)
                return (
                  <button
                    key={day.value}
                    type="button"
                    aria-pressed={selected}
                    aria-label={day.label}
                    onClick={() => toggleDay(day.value)}
                    className={cn(
                      'h-10 min-w-0 rounded-[9px] border text-[12.5px] font-medium transition-colors',
                      selected
                        ? 'border-ink bg-ink text-oncolor'
                        : 'border-line bg-surface text-ink-soft hover:border-line-strong hover:text-ink',
                    )}
                  >
                    {day.short}
                  </button>
                )
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="min-w-0">
              <span className="label-eyebrow mb-2 block">From</span>
              <input type="time" value={start} onChange={(event) => setStart(event.target.value)} className="field-box num min-w-0 px-3" />
            </label>
            <label className="min-w-0">
              <span className="label-eyebrow mb-2 block">Until</span>
              <input type="time" value={end} onChange={(event) => setEnd(event.target.value)} className="field-box num min-w-0 px-3" />
            </label>
          </div>

          <Combobox
            label="Time zone"
            appearance="box"
            options={zoneOptions}
            value={timezone}
            onChange={setTimezone}
            placeholder="Choose a time zone"
          />

          {error && (
            <p role="alert" className="text-[13px] text-danger">
              {error}
            </p>
          )}
        </div>
      )}

      <div className="flex items-center justify-end gap-2 border-t border-line px-4 py-3 sm:px-5">
        {dirty && (
          <span className="mr-auto text-[12px] text-ink-faint" aria-live="polite">
            Unsaved changes
          </span>
        )}
        <Button size="sm" onClick={() => save.mutate(draft)} disabled={!dirty || Boolean(error)} loading={save.isPending}>
          Save schedule
        </Button>
      </div>
    </section>
  )
}

/** Without Power: what Auto Apply is, and the way to get it. */
export function PowerUpsell({ onUpgrade }: { onUpgrade: () => void }) {
  const { data } = usePlans()
  const power = data?.plans.find((plan) => plan.key === 'power')
  return (
    <section className="card overflow-hidden border-brand/40" aria-label="Auto Apply is part of Power">
      <div className="bg-brand-soft/60 px-4 py-4 sm:px-5">
        <p className="label-eyebrow text-brand">Power plan</p>
        <h2 className="mt-1.5 text-[16px] font-semibold text-ink">Auto Apply is part of Power</h2>
        <p className="mt-1 text-[13px] leading-relaxed text-ink-soft">
          We apply to your matches every day without you pressing Apply, on the days and hours you choose.
        </p>
      </div>
      <ul className="space-y-1.5 px-4 py-3.5 text-[13px] text-ink-soft sm:px-5">
        <li>· {power ? `${new Intl.NumberFormat('en-US').format(power.monthlyApplications)} applications a month` : 'The biggest monthly allowance'}</li>
        <li>· Schedule the days and hours it runs</li>
        <li>· A report emailed each evening</li>
        <li>· Resume and cover letter tailored to each job</li>
      </ul>
      <div className="flex flex-wrap items-center gap-2 border-t border-line px-4 py-3 sm:px-5">
        <Button size="sm" onClick={onUpgrade}>
          Upgrade to Power{power ? ` · ${formatCents(power.prices.monthly.amountCents)}/mo` : ''}
        </Button>
        <Link to="/dashboard/plans" className="inline-flex h-9 items-center px-2 text-[13px] font-medium text-ink-soft hover:text-ink">
          Compare plans
        </Link>
      </div>
    </section>
  )
}
